import React, { useEffect, useMemo, useState } from 'react';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPost } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { formatINR } from '../../lib/format';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import {
  BarChart2, AlertTriangle, Banknote, Receipt, Calendar, RefreshCw,
  Lock, Activity, TrendingUp, Printer, Loader2,
} from 'lucide-react';

const todayMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const prevMonth = (m) => {
  const [y, mm] = m.split('-').map(Number);
  if (mm === 1) return `${y - 1}-12`;
  return `${y}-${String(mm - 1).padStart(2, '0')}`;
};

const sevTone = {
  red: 'bg-rose-50 border-rose-300 text-rose-800',
  amber: 'bg-amber-50 border-amber-300 text-amber-800',
};

const StatBlock = ({ label, value, sublabel, tone, testid }) => (
  <div className={`rounded-sm border p-4 ${tone === 'red' ? 'border-rose-200 bg-rose-50' : 'border-slate-200 bg-white'}`} data-testid={testid}>
    <div className="text-[10px] text-slate-500 uppercase font-display font-bold tracking-wider">{label}</div>
    <div className={`text-xl font-display font-extrabold mt-1 ${tone === 'red' ? 'text-rose-700' : 'text-[#0F172A]'}`}>{value}</div>
    {sublabel && <div className="text-[11px] text-slate-500 mt-1">{sublabel}</div>}
  </div>
);

const Delta = ({ d }) => {
  if (!d || d.delta === 0) return <span className="text-slate-400 text-[11px]">no change</span>;
  const pos = d.delta > 0;
  return (
    <span className={`text-[11px] font-bold ${pos ? 'text-emerald-700' : 'text-rose-700'}`}>
      {pos ? '↑' : '↓'} {formatINR(Math.abs(d.delta))}
      {d.delta_pct != null && ` (${d.delta_pct > 0 ? '+' : ''}${d.delta_pct}%)`}
    </span>
  );
};

export default function CFOPack() {
  const [month, setMonth] = useState(todayMonth());
  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [compare, setCompare] = useState(false);
  const [delta, setDelta] = useState(null);
  const [history, setHistory] = useState([]);

  const me = useAuth()?.user || {};
  const canRunClose = me.role === 'super_admin' || me.role === 'md'
    || ['MD', 'CEO', 'CMD'].includes(me.department);

  const load = async () => {
    setLoading(true);
    try {
      const p = await apiGet(`/finance/cfo-pack?month=${month}`);
      setPack(p);
      const h = await apiGet('/finance/close/history');
      setHistory(h.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load CFO pack');
    }
    setLoading(false);
  };

  const loadDelta = async () => {
    setDelta(null);
    if (!compare) return;
    try {
      const d = await apiGet(`/finance/close/compare?m1=${prevMonth(month)}&m2=${month}`);
      setDelta(d);
    } catch (e) {
      // silent — both months may not be closed
      setDelta(null);
    }
  };

  async function runClose() {
    if (!window.confirm(`This will freeze the ${month} financial position. Re-running replaces the previous close. Proceed?`)) return;
    setRunning(true);
    try {
      await apiPost(`/finance/close/run?month=${month}&reason=user-initiated`, {});
      toast.success(`Monthly close completed for ${month}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Close run failed');
    }
    setRunning(false);
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);
  useEffect(() => { loadDelta(); /* eslint-disable-next-line */ }, [compare, month]);

  const onerous = pack?.margin?.onerous_projects || [];
  const alerts = pack?.alerts || [];
  const isClosed = pack?.is_from_snapshot;
  const closeRecord = useMemo(
    () => history.find((h) => h.month === month),
    [history, month],
  );

  return (
    <div className="p-6 space-y-6 print:p-2" data-testid="cfo-pack-page">
      <div className="flex flex-wrap items-start justify-between gap-3 print:hidden">
        <PageHeader
          title="CFO Pack & Monthly Close"
          subtitle="Revenue, margin, cash, working capital, retention & statutory — one view."
          icon={BarChart2}
        />
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-sm text-sm"
            data-testid="cfo-month"
          />
          <label className="flex items-center gap-1.5 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={compare}
              onChange={(e) => setCompare(e.target.checked)}
              className="w-4 h-4 accent-[#3b4872]"
              data-testid="cfo-compare-toggle"
            />
            Compare with previous month
          </label>
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-slate-300 hover:border-[#3b4872] rounded-sm text-xs uppercase tracking-wider font-display font-bold"
            data-testid="cfo-print"
          ><Printer size={14} /> Print</button>
          {canRunClose && (
            <button
              onClick={runClose}
              disabled={running}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#3b4872] hover:bg-[#2c365a] text-white text-xs uppercase tracking-wider font-display font-bold rounded-sm disabled:opacity-50"
              data-testid="cfo-run-close"
            >
              {running ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />}
              {running ? 'Closing…' : 'Run Close'}
            </button>
          )}
        </div>
      </div>

      {/* Status banner */}
      <div className="flex items-center gap-3 text-xs">
        {isClosed ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 border border-emerald-200 rounded-sm text-emerald-800 font-display font-bold">
            <Lock size={12} /> CLOSED · {closeRecord?.run_at?.slice(0, 16) || ''} by {closeRecord?.run_by || ''}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 border border-amber-200 rounded-sm text-amber-800 font-display font-bold">
            <Activity size={12} /> LIVE — Month not yet closed
          </span>
        )}
        {compare && delta && (
          <span className="text-slate-500">Comparing <b>{delta.m1}</b> vs <b>{delta.m2}</b></span>
        )}
      </div>

      {/* Alerts strip */}
      {alerts.length > 0 && (
        <div className="space-y-2" data-testid="cfo-alerts">
          {alerts.map((a, i) => (
            <div key={i} className={`flex items-start gap-2 border rounded-sm p-3 text-sm ${sevTone[a.severity] || sevTone.amber}`}>
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <div>{a.message}</div>
            </div>
          ))}
        </div>
      )}

      {loading && <div className="text-slate-500 text-sm">Loading…</div>}

      {pack && !loading && (
        <>
          {/* 6 KPI blocks */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card data-testid="kpi-revenue">
              <CardHeader className="pb-2"><CardTitle className="text-xs uppercase font-display text-slate-500 flex items-center gap-2"><TrendingUp size={13} /> Revenue</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <StatBlock label="Recognised (POC)" value={formatINR(pack.revenue.recognised)} testid="kpi-rev-recognised" />
                <div className="grid grid-cols-2 gap-2">
                  <StatBlock label="Billed" value={formatINR(pack.revenue.billed)} />
                  <StatBlock label="Collected" value={formatINR(pack.revenue.collected)} />
                </div>
                <div className="text-[11px] text-slate-500">
                  Over/Under billing: <b className={pack.revenue.over_under_billing < 0 ? 'text-rose-700' : 'text-emerald-700'}>{formatINR(pack.revenue.over_under_billing)}</b>
                </div>
                {delta?.company?.total_revenue_recognised && <Delta d={delta.company.total_revenue_recognised} />}
              </CardContent>
            </Card>

            <Card data-testid="kpi-margin">
              <CardHeader className="pb-2"><CardTitle className="text-xs uppercase font-display text-slate-500 flex items-center gap-2"><Activity size={13} /> Margin</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <StatBlock label="Gross Margin" value={formatINR(pack.margin.gross_margin)} sublabel={pack.margin.gross_margin_pct != null ? `${pack.margin.gross_margin_pct}%` : ''} tone={pack.margin.gross_margin < 0 ? 'red' : null} testid="kpi-margin-amt" />
                {onerous.length > 0 && (
                  <div className="text-[11px] text-rose-700 font-bold">
                    {onerous.length} onerous project(s) — see below
                  </div>
                )}
                {delta?.company?.gross_margin && <Delta d={delta.company.gross_margin} />}
              </CardContent>
            </Card>

            <Card data-testid="kpi-cash">
              <CardHeader className="pb-2"><CardTitle className="text-xs uppercase font-display text-slate-500 flex items-center gap-2"><Banknote size={13} /> Cash</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <StatBlock label="Total Bank" value={formatINR(pack.cash.total_bank)} testid="kpi-cash-bank" />
                <div className="grid grid-cols-2 gap-2">
                  <StatBlock label="OD / CC Headroom" value={formatINR(pack.cash.od_cc_headroom)} />
                  <StatBlock label="BG Margin Blocked" value={formatINR(pack.cash.bg_margin_blocked)} />
                </div>
                {delta?.company?.total_bank && <Delta d={delta.company.total_bank} />}
              </CardContent>
            </Card>

            <Card data-testid="kpi-working-capital">
              <CardHeader className="pb-2"><CardTitle className="text-xs uppercase font-display text-slate-500 flex items-center gap-2"><Calendar size={13} /> Working Capital</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <StatBlock label="Debtor Days" value={`${pack.working_capital.debtor_days}d`} sublabel={`Open AR: ${formatINR(pack.working_capital.open_ar)}`} tone={pack.working_capital.debtor_days > 90 ? 'red' : null} />
                <StatBlock label="Creditor Days" value={`${pack.working_capital.creditor_days}d`} sublabel={`Open AP: ${formatINR(pack.working_capital.open_ap)}`} />
              </CardContent>
            </Card>

            <Card data-testid="kpi-retention">
              <CardHeader className="pb-2"><CardTitle className="text-xs uppercase font-display text-slate-500 flex items-center gap-2"><RefreshCw size={13} /> Retention</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <StatBlock label="Net Retention" value={formatINR(pack.retention.net)} />
                <div className="grid grid-cols-2 gap-2">
                  <StatBlock label="Receivable" value={formatINR(pack.retention.receivable)} />
                  <StatBlock label="Payable" value={formatINR(pack.retention.payable)} />
                </div>
              </CardContent>
            </Card>

            <Card data-testid="kpi-statutory">
              <CardHeader className="pb-2"><CardTitle className="text-xs uppercase font-display text-slate-500 flex items-center gap-2"><Receipt size={13} /> Statutory Due</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <StatBlock label="TDS Pending Deposit" value={formatINR(pack.statutory_due.tds_deducted_not_deposited)} tone={pack.statutory_due.tds_deducted_not_deposited > 0 ? 'red' : null} />
                <StatBlock label="GST Cash Liability (est.)" value={formatINR(pack.statutory_due.gst_cash_liability_estimate)} />
              </CardContent>
            </Card>
          </div>

          {/* Onerous projects table */}
          {onerous.length > 0 && (
            <Card data-testid="cfo-onerous-table">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-display uppercase tracking-[0.16em] text-rose-700 flex items-center gap-2">
                  <AlertTriangle size={14} /> Onerous Projects (AS-7)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-slate-600 mb-3">Expected total cost exceeds contract value — recognise the full expected loss now, not pro-rata.</p>
                <table className="w-full text-xs">
                  <thead className="text-left text-slate-500 border-b border-slate-200">
                    <tr>
                      <th className="py-1.5">Project</th>
                      <th className="py-1.5 text-right">Expected Loss</th>
                    </tr>
                  </thead>
                  <tbody>
                    {onerous.map((p) => (
                      <tr key={p.project_id} className="border-b border-slate-100">
                        <td className="py-1.5">{p.project_name}</td>
                        <td className="py-1.5 text-right text-rose-700 font-bold">{formatINR(p.expected_loss)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          {/* Close history (small) */}
          {history.length > 0 && (
            <div className="text-[11px] text-slate-500" data-testid="cfo-close-history">
              Closed months: {history.slice(0, 6).map((h) => h.month).join(' · ')}
            </div>
          )}
        </>
      )}

      <style>{`
        @media print {
          nav, header, .print\\:hidden { display: none !important; }
          body { background: white; }
        }
      `}</style>
    </div>
  );
}
